import { useState } from "react";
import { GlassCard, PillTag, Btn } from "./UI";
import Icon from "./Icon";
import { useStoreQuery, queries } from "@/lib/store";
import { useLocation } from "wouter";

/**
 * StrengthCard
 * On-brand monthly summary of a sister's logged strength work.
 * Pulls everything from `queries.strengthCard(userId, days)`.
 *
 * Two modes:
 *   - Standard: hero stat + PR list + top-3 sparklines + Kiley affirmation
 *   - Low-volume (<4 sessions): softer copy, gentler verse, encouragement
 *
 * Self-contained: takes no props, fetches `currentUser` itself.
 */
export default function StrengthCard({ days = 30 }: { days?: number }) {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. Reading it would show
  // Hannah's logged lifts and PRs as if they were this sister's own.
  const userId = me?.id ?? null;
  const card = useStoreQuery(() => (userId ? queries.strengthCard(userId, days) : null));
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((e) => !e);

  // Identity not resolved yet — show nothing rather than someone else's lifts.
  if (!userId) return null;

  if (!card) {
    // No sets at all in the window, gentle empty state
    return (
      <GlassCard
        organic
        padding={22}
        style={{
          background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
          color: "#F5F0E8",
        }}
      >
        <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", letterSpacing: "0.18em" }}>
          STRENGTH CARD
        </p>
        <h3 className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15, marginTop: 8 }}>
          A quiet season, sister.
        </h3>
        <p className="mnf-body" style={{ fontSize: 13, opacity: 0.85, marginTop: 8, lineHeight: 1.55 }}>
          When you log a few sets on the Move screen, your monthly card will start to bloom here.
        </p>
      </GlassCard>
    );
  }

  return card.isLowVolume
    ? <LowVolumeCard card={card} expanded={expanded} onToggle={toggle} />
    : <FullCard card={card} expanded={expanded} onToggle={toggle} />;
}

/* --------------------------- Collapsible header --------------------------- */

function CollapseChevron({ expanded, dark }: { expanded: boolean; dark?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        borderRadius: 999,
        background: dark ? "rgba(229,185,151,0.14)" : "rgba(30,74,53,0.08)",
        color: dark ? "rgba(229,185,151,0.95)" : "var(--mnf-f)",
        transition: "transform 220ms cubic-bezier(0.22,1,0.36,1)",
        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
      }}
    >
      <Icon name="expand_more" size={18} />
    </span>
  );
}

type CardData = NonNullable<ReturnType<typeof queries.strengthCard>>;

/* -------------------------------- Full Card -------------------------------- */

function FullCard({ card, expanded, onToggle }: { card: CardData; expanded: boolean; onToggle: () => void }) {
  const affirmation = buildAffirmation(card);
  const [, setLocation] = useLocation();
  return (
    <GlassCard
      organic
      padding={22}
      style={{
        background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
        color: "#F5F0E8",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Soft gold orb for warmth */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -40,
          right: -30,
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(229,185,151,0.22), rgba(229,185,151,0) 70%)",
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls="strength-card-body"
        data-testid="button-strength-card-toggle"
        className="flex items-center justify-between w-full text-left"
        style={{ position: "relative", background: "transparent", border: "none", padding: 0, color: "inherit", cursor: "pointer" }}
      >
        <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", letterSpacing: "0.18em", fontSize: 10 }}>
          STRENGTH CARD · {card.monthLabel.toUpperCase()}
        </p>
        <span className="flex items-center gap-2">
          <PillTag tone="gold">{card.sessions} honest days</PillTag>
          <CollapseChevron expanded={expanded} dark />
        </span>
      </button>

      {!expanded && (
        <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1, marginTop: 14, position: "relative" }}>
          {card.totalLb.toLocaleString()}
          <span className="mnf-body" style={{ fontSize: 13, fontWeight: 500, marginLeft: 8, opacity: 0.85 }}>
            lb lifted · {card.prs.length} PR{card.prs.length === 1 ? "" : "s"}
          </span>
        </p>
      )}

      {expanded && (
      <div id="strength-card-body">
      <div style={{ marginTop: 14, position: "relative" }}>
        <h3 className="mnf-serif" style={{ fontSize: 38, fontWeight: 500, lineHeight: 1, letterSpacing: "-0.01em" }}>
          {card.totalLb.toLocaleString()}
          <span className="mnf-body" style={{ fontSize: 14, fontWeight: 500, marginLeft: 8, opacity: 0.85, letterSpacing: 0 }}>
            lb lifted
          </span>
        </h3>
        <p className="mnf-serif italic" style={{ fontSize: 13, marginTop: 10, opacity: 0.92, lineHeight: 1.5 }}>
          "She is clothed with strength and dignity."
          <span style={{ color: "rgba(229,185,151,0.95)" }}> · Proverbs 31:25</span>
        </p>
      </div>

      {/* Stat tiles */}
      <div
        className="grid grid-cols-3"
        style={{
          marginTop: 18,
          paddingTop: 14,
          paddingBottom: 14,
          borderTop: "1px solid rgba(229,185,151,0.18)",
          borderBottom: "1px solid rgba(229,185,151,0.18)",
          gap: 4,
        }}
      >
        <StatTile label="PRs set" value={card.prs.length.toString()} />
        <StatTile label="Sessions" value={card.sessions.toString()} center />
        <StatTile label="Total reps" value={card.totalReps.toString()} />
      </div>

      {/* PR list */}
      {card.prs.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", letterSpacing: "0.16em", fontSize: 10 }}>
            PERSONAL RECORDS
          </p>
          <ul style={{ marginTop: 8 }}>
            {card.prs.map((pr) => (
              <li
                key={pr.name}
                className="flex items-center justify-between"
                style={{ padding: "8px 0", borderBottom: "1px dashed rgba(229,185,151,0.18)" }}
              >
                <span className="mnf-body" style={{ fontSize: 13, fontWeight: 500 }}>
                  {pr.name}
                </span>
                <span className="mnf-serif" style={{ fontSize: 14, fontWeight: 500, color: "rgba(229,185,151,1)" }}>
                  {pr.weightLb} lb × {pr.reps}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top lifts with sparklines */}
      {card.topLifts.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", letterSpacing: "0.16em", fontSize: 10 }}>
            PROGRESSION · TOP LIFTS
          </p>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            {card.topLifts.map((l) => (
              <Sparkline key={l.id} name={l.name} series={l.series} startLb={l.startLb} endLb={l.endLb} deltaLb={l.deltaLb} />
            ))}
          </div>
        </div>
      )}

      {/* Kiley affirmation */}
      <div
        style={{
          marginTop: 18,
          padding: "12px 14px",
          borderRadius: 16,
          background: "rgba(229,185,151,0.10)",
          border: "1px solid rgba(229,185,151,0.18)",
        }}
      >
        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: 999,
              background: "rgba(229,185,151,0.95)",
              color: "#1E4A35",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            K
          </span>
          <span className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", fontSize: 9, letterSpacing: "0.16em" }}>
            KILEY SAYS
          </span>
        </div>
        <p className="mnf-serif italic" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {affirmation}
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <Btn
          variant="coral"
          size="md"
          style={{ width: "100%" }}
          onClick={() => setLocation("/share-journey")}
          data-testid="button-strength-share"
        >
          <Icon name="ios_share" size={16} />
          <span style={{ marginLeft: 6 }}>Share to your sisters</span>
        </Btn>
      </div>
      </div>
      )}
    </GlassCard>
  );
}

/* ------------------------------ Low-Volume Card ----------------------------- */

function LowVolumeCard({ card, expanded, onToggle }: { card: CardData; expanded: boolean; onToggle: () => void }) {
  return (
    <GlassCard
      organic
      padding={22}
      style={{
        background: "linear-gradient(165deg, #F5F0E8 0%, #EDE5D6 100%)",
        color: "var(--mnf-fd)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls="strength-card-body-low"
        data-testid="button-strength-card-toggle"
        className="flex items-center justify-between w-full text-left"
        style={{ background: "transparent", border: "none", padding: 0, color: "inherit", cursor: "pointer" }}
      >
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.18em", fontSize: 10 }}>
          STRENGTH CARD · {card.monthLabel.toUpperCase()}
        </p>
        <span className="flex items-center gap-2">
          <PillTag tone="fiber">A gentle season</PillTag>
          <CollapseChevron expanded={expanded} />
        </span>
      </button>

      {!expanded && (
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", marginTop: 10, lineHeight: 1.5 }}>
          {card.sessions} session{card.sessions === 1 ? "" : "s"} this month. Rest is part of the rhythm.
        </p>
      )}

      {expanded && (
      <div id="strength-card-body-low">
      <h3 className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15, marginTop: 12 }}>
        A gentle {card.monthLabel}, sister.
      </h3>
      <p className="mnf-serif italic" style={{ fontSize: 13, marginTop: 8, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        "He makes me lie down in green pastures, he leads me beside quiet waters."
        <span style={{ color: "var(--mnf-t)" }}> · Psalm 23:2</span>
      </p>

      <div
        className="grid grid-cols-3"
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: "1px solid rgba(30,74,53,0.10)",
          gap: 4,
        }}
      >
        <StatTile dark label="Sessions" value={card.sessions.toString()} />
        <StatTile dark label="Total lb" value={card.totalLb.toLocaleString()} center />
        <StatTile dark label="PRs" value={card.prs.length.toString()} />
      </div>

      <p className="mnf-body" style={{ fontSize: 13, marginTop: 14, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        Rest is part of the rhythm. When you're ready, your weights are waiting.
      </p>
      </div>
      )}
    </GlassCard>
  );
}

/* --------------------------------- Helpers --------------------------------- */

function StatTile({
  label,
  value,
  center = false,
  dark = false,
}: {
  label: string;
  value: string;
  center?: boolean;
  dark?: boolean;
}) {
  const borderColor = dark ? "rgba(30,74,53,0.10)" : "rgba(229,185,151,0.18)";
  const labelColor = dark ? "var(--mnf-tg)" : "rgba(245,240,232,0.75)";
  const valueColor = dark ? "var(--mnf-fd)" : "#F5F0E8";
  return (
    <div
      className="text-center"
      style={{
        borderLeft: center ? `1px solid ${borderColor}` : "none",
        borderRight: center ? `1px solid ${borderColor}` : "none",
        padding: "0 4px",
      }}
    >
      <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: valueColor, lineHeight: 1 }}>
        {value}
      </p>
      <p className="mnf-eyebrow" style={{ color: labelColor, fontSize: 9, marginTop: 4, letterSpacing: "0.14em" }}>
        {label}
      </p>
    </div>
  );
}

function Sparkline({
  name,
  series,
  startLb,
  endLb,
  deltaLb,
}: {
  name: string;
  series: number[];
  startLb: number;
  endLb: number;
  deltaLb: number;
}) {
  const w = 80;
  const h = 22;
  const min = Math.min(...series, startLb);
  const max = Math.max(...series, endLb);
  const range = Math.max(1, max - min);
  const points =
    series.length === 1
      ? `0,${h / 2} ${w},${h / 2}`
      : series
          .map((v, i) => {
            const x = (i / (series.length - 1)) * w;
            const y = h - ((v - min) / range) * h;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");
  const climbed = deltaLb > 0;
  return (
    <div className="flex items-center gap-3">
      <span className="mnf-body" style={{ fontSize: 12, fontWeight: 500, flex: 1, lineHeight: 1.2 }}>
        {name}
      </span>
      <svg width={w} height={h} aria-hidden style={{ flexShrink: 0 }}>
        <polyline
          points={points}
          fill="none"
          stroke="rgba(229,185,151,0.95)"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="mnf-serif"
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: climbed ? "rgba(229,185,151,1)" : "rgba(245,240,232,0.7)",
          minWidth: 52,
          textAlign: "right",
        }}
      >
        {startLb} to {endLb} lb
      </span>
    </div>
  );
}

function buildAffirmation(card: CardData): string {
  const climbing = card.topLifts.find((l) => l.deltaLb > 0);
  if (climbing) {
    return `Your ${climbing.name.toLowerCase()} climbed ${climbing.deltaLb} lb this month, sister. That is faithfulness in motion.`;
  }
  if (card.prs.length > 0) {
    const pr = card.prs[0];
    return `${card.prs.length === 1 ? "One personal record" : `${card.prs.length} personal records`} this month, including a ${pr.weightLb} lb ${pr.name.toLowerCase()}. Glorious work.`;
  }
  return "You showed up, and that is the rep that counts most. Keep going gently.";
}
